/**
 * RUN T — T-1. The sidebar patient typeahead.
 *
 * What was here before: a <div> with `onClick={() => navigate('/patients')}`.
 * You could not type into it, there was no dropdown, and the ⌘K hint beside
 * it was decorative — no handler for that chord existed anywhere in the app.
 *
 * What it does now: two characters open a dropdown of up to eight matching
 * patients, each row carrying the things you need to know you have the right
 * person — name, MRN, date of birth, age, when they were last in, when they
 * are next in. Arrows move, Enter opens, Escape closes, ⌘K focuses. Enter
 * with nothing highlighted still goes to the Patients page filtered by what
 * you typed, which is what the old control did and what muscle memory expects.
 *
 * PHI: the query goes in a POST body, never in the URL. See patientSearchApi.
 *
 * Incident I-1 Part B — the result list is now a CARD in the style of
 * PracticeHub's search dropdown. It used to be positioned inside the sidebar,
 * so it could never be wider than the sidebar and its loading / empty / error
 * lines sat over the sidebar's own labels. It now renders through a portal,
 * anchored under the search box, at least 480 px wide, floating over the main
 * content. Each row: the name with the typed text in bold, the MRN in
 * brackets, the date of birth in the clinic's date format and a status pill on
 * the left; "Last Appt:" and "Next Appt:" with weekday and date on the right.
 * Keyboard behaviour is exactly as before.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DEBOUNCE_MS, MAX_SUGGESTIONS, fallbackPath, isFocusChord,
  nextIndex, patientPath, shouldQuery, stateForResults,
  cardPosition, formatApptDate, formatClinicDate, highlightParts,
  type PatientSuggestion, type TypeaheadState,
} from './patientSearch';
import { useClinicBranding } from '../../hooks/useClinicBranding';
import { searchPatientSuggestions } from './patientSearchApi';

export function PatientTypeahead(): JSX.Element {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { dateFormat } = useClinicBranding();
  const [text, setText] = useState('');
  const [state, setState] = useState<TypeaheadState>({ kind: 'idle' });
  const [highlight, setHighlight] = useState(-1);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** The bordered search box — the card is anchored to its bottom-left corner. */
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(null);
  /** Bumped on every keystroke; a response from an older token is dropped. */
  const requestToken = useRef(0);

  const results = state.kind === 'results' ? state.results : [];

  // ⌘K / Ctrl-K focuses the box. The hint has been printed next to this
  // control since it was built; this is the first time it does anything.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isFocusChord(e)) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Debounced lookup. Every keystroke cancels the pending timer AND
  // invalidates any request already in flight, so a slow response for "ma"
  // can never overwrite the results for "maria".
  useEffect(() => {
    if (!shouldQuery(text)) {
      requestToken.current += 1;
      setState({ kind: 'idle' });
      setHighlight(-1);
      return;
    }
    const token = ++requestToken.current;
    setState({ kind: 'loading' });
    const timer = setTimeout(() => {
      void searchPatientSuggestions(text.trim())
        .then((rows) => {
          if (requestToken.current !== token) return;   // stale
          setState(stateForResults(rows));
          setHighlight(-1);
        })
        .catch(() => {
          if (requestToken.current !== token) return;
          setState({ kind: 'error' });
          setHighlight(-1);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const close = useCallback(() => {
    setOpen(false);
    setHighlight(-1);
  }, []);

  const choose = useCallback((s: PatientSuggestion) => {
    close();
    setText('');
    setState({ kind: 'idle' });
    navigate(patientPath(s.id));
  }, [close, navigate]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => nextIndex(h, results.length, e.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const picked = highlight >= 0 ? results[highlight] : undefined;
      if (picked) { choose(picked); return; }
      // No highlight: the old behaviour, kept — the Patients page, filtered.
      if (text.trim()) { close(); navigate(fallbackPath(text)); }
    }
  };

  const showDropdown = open && shouldQuery(text);
  const listId = 'sidebar-patient-search-list';

  // Anchor the card under the box; follow it on resize and on any scroll
  // (the sidebar itself can scroll).
  useLayoutEffect(() => {
    if (!showDropdown) { setPos(null); return; }
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos(cardPosition({ left: r.left, bottom: r.bottom, width: r.width }, { width: window.innerWidth, height: window.innerHeight }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [showDropdown]);

  const lang = i18n?.language || 'en';

  const statusLabel = useMemo(() => ({
    active: t('patients.status.active', 'Active'),
    do_not_contact: t('patients.status.doNotContact', 'Do Not Contact'),
  }), [t]);

  return (
    <div className="mx-3 mb-4 relative">
      <div ref={anchorRef} className="px-3 py-2 rounded-md bg-surface-active border border-charcoal-200 text-xs flex items-center gap-2 focus-within:border-charcoal-400">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-charcoal-500 flex-shrink-0">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          data-testid="sidebar-patient-search"
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={highlight >= 0 ? `${listId}-${highlight}` : undefined}
          autoComplete="off"
          value={text}
          placeholder={t('patients.searchPlaceholder', 'Search patients...')}
          // Focus opens nothing and navigates nowhere — it only makes the box
          // ready. The control used to jump to /patients the moment you
          // touched it.
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(close, 120)}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          className="bg-transparent outline-none flex-1 min-w-0 text-charcoal-900 placeholder:text-charcoal-500"
        />
        <kbd className="ml-auto text-[9px] px-1 border border-charcoal-300 rounded text-charcoal-400 flex-shrink-0">⌘K</kbd>
      </div>

      {showDropdown && pos && createPortal(
        <div
          id={listId}
          role="listbox"
          data-testid="sidebar-patient-search-dropdown"
          // Clicking the card (a scrollbar, a gap) must not blur the box and close it.
          onMouseDown={(e) => e.preventDefault()}
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight }}
          className="z-[1000] overflow-y-auto bg-white border border-charcoal-200 rounded-lg shadow-2xl"
        >
          {state.kind === 'loading' && (
            <div data-testid="typeahead-loading" className="px-4 py-3 text-sm text-charcoal-500">
              {t('patients.typeahead.loading', 'Searching…')}
            </div>
          )}
          {state.kind === 'empty' && (
            <div data-testid="typeahead-empty" className="px-4 py-3 text-sm text-charcoal-500">
              {t('patients.typeahead.empty', 'No matching patients')}
            </div>
          )}
          {state.kind === 'error' && (
            <div data-testid="typeahead-error" className="px-4 py-3 text-sm text-red-700">
              {t('patients.typeahead.error', 'Search is unavailable right now')}
            </div>
          )}
          {state.kind === 'results' && results.slice(0, MAX_SUGGESTIONS).map((s, i) => (
            <button
              key={s.id}
              id={`${listId}-${i}`}
              role="option"
              type="button"
              aria-selected={i === highlight}
              data-testid="typeahead-row"
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-4 py-2.5 border-b border-charcoal-100 last:border-b-0 flex items-start gap-4 transition-colors ${
                i === highlight ? 'bg-sky-50' : 'bg-white hover:bg-sky-50'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span data-testid="typeahead-name" className="text-sm text-charcoal-900 truncate">
                    {highlightParts(s.name, text).map((part, k) => (
                      part.match ? <strong key={k} className="font-semibold">{part.text}</strong> : <span key={k}>{part.text}</span>
                    ))}
                    {s.mrn && <span className="text-charcoal-500"> ({s.mrn})</span>}
                  </span>
                  {s.status && (
                    <span
                      data-testid="typeahead-status"
                      className={`flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        s.status === 'do_not_contact'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}
                    >
                      {statusLabel[s.status]}
                    </span>
                  )}
                </div>
                <div data-testid="typeahead-dob" className="text-xs text-charcoal-500 mt-0.5">
                  {formatClinicDate(s.dateOfBirth, dateFormat)}
                </div>
              </div>
              <div className="flex-shrink-0 text-right text-xs text-charcoal-600 leading-5">
                <div data-testid="typeahead-last">
                  <span className="text-charcoal-500">{t('patients.typeahead.lastAppt', 'Last Appt:')}</span>{' '}
                  {formatApptDate(s.lastAppointmentAt, dateFormat, lang)}
                </div>
                <div data-testid="typeahead-next">
                  <span className="text-charcoal-500">{t('patients.typeahead.nextAppt', 'Next Appt:')}</span>{' '}
                  {formatApptDate(s.nextAppointmentAt, dateFormat, lang)}
                </div>
              </div>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default PatientTypeahead;
